/**
 * Event Hub data layer.
 *
 * For now the event.json is a static fixture in public/events/<id>.json.
 * Later a backend endpoint will serve it - swap the URL in fetchEvent() only,
 * every view/component consumes the returned object unchanged.
 */
import axios from 'axios';

// Content Credentials normalizer lives in shared utils (used app-wide); re-export
// so event-hub components keep a single import surface alongside versionInfo.
export { normalizeProvenance } from '../../utils/provenance';

export function fetchEvent(id) {
  const base = process.env.BASE_URL || '/';
  return axios
    .get(`${base}events/${encodeURIComponent(id)}.json`)
    .then((res) => res.data);
}

/* ---------- shared helpers (ported from static app.js) ---------- */

// "00:08:14" | "494.2" -> seconds
export function secOf(ts) {
  const p = String(ts == null ? 0 : ts)
    .replace(',', '.')
    .split(':')
    .map(parseFloat);
  if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
  if (p.length === 2) return p[0] * 60 + p[1];
  return p[0] || 0;
}

// derive a jpg poster from a media filename when posterUrl is absent
export function posterOf(file) {
  return (file || '').replace(/\.(mp4|webm)$/i, '.jpg');
}

export function isAudioFile(file) {
  return /\.(m4a|mp3|aac)$/i.test(file || '');
}

// a representative recording thumbnail: prefer an AI-highlight clip frame (curated
// content - speakers/room) over the raw recording poster, which can land on a
// slide/break screen; then a camera frame; then the raw poster as a last resort.
export function posterFor(rec) {
  if (!rec) return '';
  const els = rec.elements || {};
  const clip = (els.clips || []).find((c) => c.posterUrl);
  const cam = (els.cams || []).find((c) => c.posterUrl);
  return (clip && clip.posterUrl) || (cam && cam.posterUrl) || rec.posterUrl || '';
}

// sentiment -> css modifier class used by .tag-s / legend
const SENT = { positive: 'pos', negative: 'neg', mixed: 'mix', neutral: 'neu' };
export function sentClass(s) {
  return SENT[String(s || 'neutral').toLowerCase()] || 'neu';
}

// QAPO metadata (plain-word labels)
export const QAPO = [
  { key: 'Q', name: 'Quotes' },
  { key: 'A', name: 'Answers' },
  { key: 'P', name: 'Pain points' },
  { key: 'O', name: 'Observations' },
];

// a usable quote? (server sometimes writes the literal string "null")
export function hasQuote(v) {
  const q = v && v.quote;
  return !!q && String(q).toLowerCase() !== 'null' && String(q).trim().length > 8;
}

// short calendar date from a ms timestamp, e.g. "Jul 2"
export function fmtDateShort(ms) {
  try {
    return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (e) {
    return '';
  }
}

// the strongest highlight clips across every recording, tagged with their recording id
// and session label so the hub can deep-link into View B ("Open in recording →").
//
// Selection prefers clips carrying a real quote, but DISPLAY is always chronological:
// recordings run in fixture order (which is chronological) and clips run by timestamp
// within each. Sorting the whole list by quote-richness, as this used to, scrambled a
// multi-session event into an order no reader could follow.
export function topClips(recordings, n) {
  const all = [];
  (recordings || []).forEach((r, ri) => {
    const clips = (r.elements && r.elements.clips) || [];
    clips
      .slice()
      .sort((a, b) => secOf(a.timestamp) - secOf(b.timestamp))
      .forEach((c, ci) => {
        all.push(
          Object.assign({}, c, {
            _recId: r.id,
            _recTag: r.tag || '',
            _recTitle: r.title || '',
            _order: ri * 1e6 + ci,
          })
        );
      });
  });
  if (typeof n !== 'number' || all.length <= n) return all;

  // Take from each recording in turn, so a short opening session cannot crowd out the
  // main one. (Selecting purely by quote-richness put all 8 moments of a 3-session
  // event in sessions 1 and 2, hiding the 44-minute session entirely.)
  const byRec = [];
  const index = {};
  all.forEach((c) => {
    if (index[c._recId] === undefined) {
      index[c._recId] = byRec.length;
      byRec.push([]);
    }
    byRec[index[c._recId]].push(c);
  });
  // within a recording, prefer clips carrying a real quote
  byRec.forEach((g) =>
    g.sort((a, b) => (hasQuote(b) ? 1 : 0) - (hasQuote(a) ? 1 : 0) || a._order - b._order)
  );

  // share the slots out in proportion to what each recording has, but never leave a
  // recording with nothing - a 44-minute session should not get fewer moments than
  // the 4-minute one that opened the day.
  const quota = byRec.map((g) => Math.min(g.length, Math.max(1, Math.round((n * g.length) / all.length))));
  let slack = n - quota.reduce((a, b) => a + b, 0);
  for (let pass = 0; slack !== 0 && pass < byRec.length * 2; pass += 1) {
    const g = pass % byRec.length;
    if (slack > 0 && quota[g] < byRec[g].length) {
      quota[g] += 1;
      slack -= 1;
    } else if (slack < 0 && quota[g] > 1) {
      quota[g] -= 1;
      slack += 1;
    }
  }

  const picked = [];
  byRec.forEach((g, i) => picked.push(...g.slice(0, quota[i])));
  // …then put them back in the order they actually happened
  return picked.sort((a, b) => a._order - b._order);
}

/**
 * Media versioning normalizer.
 * The recording's top-level media/transcript/elements/analysis ARE the current
 * (renderable) version's assets. `versions` only adds metadata + the immutable
 * "originals" download list. Everything degrades to today's behavior when absent.
 */
export function versionInfo(rec) {
  const versions = (rec && Array.isArray(rec.versions) && rec.versions) || [];
  const currentId = (rec && rec.currentVersion) || null;
  const primary =
    versions.find((v) => v.primary) ||
    versions.find((v) => v.id === currentId) ||
    versions.find((v) => v.kind === 'optimized') ||
    null;
  const originals = versions.filter((v) => v.kind === 'original');
  // renderable = versions whose assets the player/insights could show (future v1.2 switching)
  const renderable = versions.filter((v) => v.primary || v.kind === 'optimized');
  return {
    hasVersions: versions.length > 0,
    currentId: currentId || (primary && primary.id) || null,
    primary,
    originals,
    renderable,
    versions,
    // cameras are v1 originals once versions exist → don't also show them as a primary tab
    camsAreOriginals: originals.some((v) => (v.downloads || []).some((d) => /camera/i.test(d.group || ''))),
  };
}

// one-line human read of the sentiment mix, e.g. "Mostly neutral, warmly positive."
export function sentimentCaption(s) {
  s = s || {};
  const pos = s.positive || 0;
  const neu = s.neutral || 0;
  const neg = s.negative || 0;
  const mix = s.mixed || 0;
  const total = pos + neu + neg + mix;
  if (!total) return '';
  const pairs = [
    ['positive', pos],
    ['neutral', neu],
    ['negative', neg],
    ['mixed', mix],
  ].sort((a, b) => b[1] - a[1]);
  const [topName, topN] = pairs[0];
  const share = topN / total;
  const lead =
    share >= 0.6 ? `Mostly ${topName}` : share >= 0.4 ? `Largely ${topName}` : `A ${topName} lean`;
  // second-strongest as warmth/tension context
  const second = pairs[1] && pairs[1][1] > 0 ? pairs[1][0] : null;
  if (!second) return `${lead}.`;
  const warmth =
    second === 'positive'
      ? 'warmly positive in places'
      : second === 'negative'
      ? 'with some friction'
      : second === 'mixed'
      ? 'with mixed moments'
      : 'evenly measured';
  return `${lead}, ${warmth}.`;
}
